import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import {
  BillingAcceptanceAdvancedDocument,
  BillingAcceptanceAdvancedStartSigningMutation,
  BillingAcceptanceAdvancedStartSigningMutationVariables,
  BillingAcceptanceAdvancedStartSigningDocument,
} from 'domain/api/graphql/generated';

import BrowserUtils from '@zvuk-b2b/react-uikit/toolkit/BrowserUtils';

import {
  startAcceptanceAdvancedSigning,
  acceptanceAdvancedStatusUpdated,
  focusAcceptanceAdvancedTab,
  setAcceptanceAdvancedTab,
  setAcceptanceAdvancedTabUrl,
  DEFAULT_ACCEPTANCE_ADVANCED_ID,
  acceptanceAdvancedSigningLoadingChanged,
} from '.';
import { getMoscowTimeToday, LinkExpirationManager, pollAcceptanceAdvancedStatus } from './utils';
import { CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS } from './consts';
import { stopGlobalPolling } from '../globalPollingSlice';

type CreateAcceptanceAdvancedListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

export const createAcceptanceAdvancedListener: CreateAcceptanceAdvancedListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => {
  const middleware = createListenerMiddleware();

  const expirationManager = new LinkExpirationManager();

  middleware.startListening({
    actionCreator: startAcceptanceAdvancedSigning,
    effect: async (action, { dispatch }) => {
      const {
        acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID,
        companyId,
        uriForReturn,
        fallbackFn,
      } = action.payload;

      if (CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.has(acceptanceAdvancedId)) {
        const context = CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.get(acceptanceAdvancedId);

        if (context?.acceptanceAdvancedTab && !context.acceptanceAdvancedTab.closed) {
          context.acceptanceAdvancedTab.focus();
          dispatch(focusAcceptanceAdvancedTab(acceptanceAdvancedId));
          dispatch(acceptanceAdvancedSigningLoadingChanged({ acceptanceAdvancedId }));

          return;
        }

        context?.abortController.abort();
        expirationManager.cancelExpiration(acceptanceAdvancedId);
        CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.delete(acceptanceAdvancedId);
      }

      try {
        const mutationResult = await apolloClient.mutate<
          BillingAcceptanceAdvancedStartSigningMutation,
          BillingAcceptanceAdvancedStartSigningMutationVariables
        >({
          mutation: BillingAcceptanceAdvancedStartSigningDocument,
          variables: {
            input: { companyId, uriForReturn },
          },
          refetchQueries: [BillingAcceptanceAdvancedDocument],
        });

        const link = mutationResult.data?.result?.confirmLink;

        if (link) {
          dispatch(acceptanceAdvancedSigningLoadingChanged({ acceptanceAdvancedId }));
          dispatch(setAcceptanceAdvancedTabUrl({ acceptanceAdvancedId, url: link }));

          const abortController = new AbortController();

          if (BrowserUtils.isIOS || BrowserUtils.isSafariDesktop) {
            CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.set(acceptanceAdvancedId, {
              abortController,
              acceptanceAdvancedTab: null,
              acceptanceAdvancedTabUrl: link,
              expiresAt: null,
            });

            fallbackFn();

            return;
          }

          const acceptanceAdvancedTab = window.open(link, '_blank');

          const linkExpiresAt = getMoscowTimeToday(24);
          expirationManager.scheduleExpiration(acceptanceAdvancedId, linkExpiresAt);

          CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.set(acceptanceAdvancedId, {
            abortController,
            acceptanceAdvancedTab,
            acceptanceAdvancedTabUrl: link,
            expiresAt: linkExpiresAt,
          });

          if (acceptanceAdvancedTab) {
            await pollAcceptanceAdvancedStatus({
              acceptanceAdvancedId,
              acceptanceAdvancedTab,
              companyId,
              dispatch,
              signal: abortController.signal,
            });
          }
        }
      } catch (error) {
        dispatch(
          acceptanceAdvancedStatusUpdated({
            acceptanceAdvancedId,
            error: (error as Error).message,
          })
        );

        dispatch(acceptanceAdvancedSigningLoadingChanged({ acceptanceAdvancedId }));

        expirationManager.cancelExpiration(acceptanceAdvancedId);
      }
    },
  });

  middleware.startListening({
    actionCreator: setAcceptanceAdvancedTab,
    effect: async (action, { dispatch }) => {
      const { acceptanceAdvancedId = DEFAULT_ACCEPTANCE_ADVANCED_ID, openedWindow, companyId } = action.payload;
      const context = CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.get(acceptanceAdvancedId);

      if (openedWindow && context) {
        const linkExpiresAt = getMoscowTimeToday(24);
        expirationManager.scheduleExpiration(acceptanceAdvancedId, linkExpiresAt);

        CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.set(acceptanceAdvancedId, {
          ...context,
          acceptanceAdvancedTab: openedWindow,
          expiresAt: linkExpiresAt,
        });

        await pollAcceptanceAdvancedStatus({
          acceptanceAdvancedId,
          acceptanceAdvancedTab: openedWindow,
          companyId,
          dispatch,
          signal: context.abortController.signal,
        });
      }
    },
  });

  middleware.startListening({
    actionCreator: stopGlobalPolling,
    effect: async (action, { dispatch }) => {
      const { id: acceptanceAdvancedId, reason } = action.payload;

      const context = CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.get(acceptanceAdvancedId);

      if (context && reason) {
        dispatch(
          acceptanceAdvancedStatusUpdated({
            acceptanceAdvancedId,
            error: reason,
          })
        );
      }
    },
  });

  middleware.startListening({
    predicate: (action) => action.type.endsWith('/clearAllAcceptanceAdvancedProcesses'),
    effect: () => {
      CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.forEach((context, id) => {
        context.abortController.abort();
        expirationManager.cancelExpiration(id);
      });

      CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.clear();
    },
  });

  return middleware;
};
